import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import TpDialog from '../shared/tp_dialog';
import { post_rule_change } from './api';
import {
  APPROVAL_REASON_TEXT,
  ERROR_TEXT,
  LIMITS,
  create_body,
  day_label,
  edited_rule,
  editable_fields,
  expected_approval,
  first_error_key,
  form_errors,
  initial_values,
  prepare_again_of,
  refusal,
  request_id_for,
  scheduled_for,
  teacher_visible,
  text_length,
  unit_suffix
} from './logic';
import ChangePanel from './change_panel';

/*
 * PROD-7828 R14 P4 — the value-edit form for a rule in force (plan §5.2 minimal, §13.6): its numbers
 * within the §6 ranges, its wording, the effective date, a note and the teacher notice. "Preview"
 * creates the change (POST /rules/changes) and the dialog then shows its preview and "Save change".
 * props: version (the in-force version view), list (logic.list_view), names, catalogue, now(), poll_ms,
 * new_request_id, prefill (logic.prepare_again_of's form: "Prepare again"), on_close, on_saved(change),
 * on_rules_changed(), on_error(e, text).
 */
const empty_form = (fields, prefill = null) => {
  const base = {
    values: initial_values(fields),
    effective_date: '',
    note: '',
    notice_mode: '',
    teacher_notice: '',
    no_notice_reason: ''
  };
  if (!prefill) return base;
  return { ...base, ...prefill, values: { ...base.values, ...(prefill.values || {}) } };
};

const range_hint = (f) => {
  if (f.input === 'select' || f.input === 'time' || f.input === 'text' || f.input === 'textarea')
    return f.hint;
  const range =
    f.max !== null && f.max !== undefined
      ? `Allowed: ${f.min || 0} to ${f.max}${unit_suffix(f.unit)}.`
      : '';
  return [range, f.hint].filter(Boolean).join(' ');
};

const value_dom_id = (field_id) => `rx-f-${field_id.replace(/[^A-Za-z0-9]/g, '-')}`;

// Form error key -> the control that takes focus for it ('values' has none, so its message does).
const FORM_DOM_ID = {
  values: 'rx-values-err',
  effective_date: 'rx-date',
  note: 'rx-note',
  notice_mode: 'rx-notice-yes',
  teacher_notice: 'rx-notice-text',
  no_notice_reason: 'rx-no-notice'
};

// aria props for a control with an optional hint and a shown error.
const described = (id, hint_id, error) => ({
  'aria-invalid': error ? 'true' : undefined,
  'aria-describedby': [hint_id, error ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined
});

const ValueField = ({ f, value, error, on_change, on_blur }) => {
  const id = value_dom_id(f.id);
  const hint = range_hint(f);
  const common = {
    id,
    value,
    ...described(id, hint ? `${id}-hint` : '', error),
    onChange: (e) => on_change(e.target.value),
    onBlur: on_blur
  };
  let input;
  if (f.input === 'textarea') input = <textarea className="tp-textarea" {...common} />;
  else if (f.input === 'select') {
    input = (
      <select className="tp-select" {...common}>
        {f.choices.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
      </select>
    );
  } else if (f.input === 'time') input = <input type="time" className="tp-input" {...common} />;
  else if (f.input === 'text') input = <input className="tp-input" {...common} />;
  else input = <input className="tp-input" inputMode="decimal" {...common} />;
  return (
    <div className="tp-field">
      <label htmlFor={id}>{f.label}</label>
      {input}
      {hint ? (
        <span id={`${id}-hint`} className="tp-hint">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-err`} className="tp-err">
          {error}
        </span>
      ) : null}
    </div>
  );
};

const EditForm = ({
  version,
  list,
  catalogue,
  prefill,
  new_request_id,
  on_created,
  on_rules_changed,
  on_error,
  set_busy,
  set_dirty
}) => {
  const rule = version.rule;
  const fields = useMemo(() => editable_fields(rule, catalogue), [rule, catalogue]);
  const [form, setForm] = useState(() => empty_form(fields, prefill));
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState(null);
  // { sig, id }: kept while the request content is unchanged, so a retry replays rather than duplicates.
  const idem = useRef(null);
  // Set on an invalid submit; the effect focuses it once the errors have rendered.
  const focus_to = useRef('');
  const [focus_tick, setFocusTick] = useState(0);
  useEffect(() => {
    const el = focus_to.current && document.getElementById(focus_to.current);
    if (el) el.focus();
    focus_to.current = '';
  }, [focus_tick]);

  const ctx = {
    fields,
    rule,
    earliest: list.earliest_allowed_date,
    latest: list.latest_allowed_date
  };
  const errors = form_errors(form, ctx);
  const valid = Object.keys(errors).length === 0;
  const edited = edited_rule(rule, fields, form.values);
  const visible = teacher_visible(rule) || teacher_visible(edited);
  const approvals = expected_approval(form, rule, edited, list.today);
  const later = scheduled_for(list.scheduled, version.rule_key);
  const values = fields.filter((f) => f.section === 'value');
  const wording = fields.filter((f) => f.section === 'wording');
  const show = (k) => (submitted || touched[k] ? errors[k] : '');
  // The notice choice is one radio group, so each radio carries the group's error.
  const notice_mode_err = show('notice_mode');
  const notice_mode_aria = {
    'aria-invalid': notice_mode_err ? 'true' : undefined,
    'aria-describedby': notice_mode_err ? 'rx-notice-mode-err' : undefined
  };

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setFailure(null);
    set_dirty(true);
  };
  const set_value = (id, v) => set({ values: { ...form.values, [id]: v } });
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (sending) return;
    if (!valid) {
      const ids = [...values, ...wording].map((f) => f.id);
      const key = first_error_key(errors, ids);
      focus_to.current = ids.indexOf(key) !== -1 ? value_dom_id(key) : FORM_DOM_ID[key] || '';
      setFocusTick((t) => t + 1);
      return;
    }
    const body = create_body({
      client_request_id: '',
      base_rule_set_hash: list.base_rule_set_hash,
      form,
      rule,
      fields
    });
    delete body.client_request_id;
    idem.current = request_id_for(idem.current, body, new_request_id);
    setSending(true);
    set_busy(true);
    setFailure(null);
    post_rule_change({ client_request_id: idem.current.id, ...body })
      .then((change) => {
        idem.current = null;
        on_created(change);
      })
      .catch((err) => {
        const r = refusal(err, 'Could not start the preview. Try again.');
        if (r.permission && on_error) on_error(err, r.text);
        // The id is retired here, so pressing Preview again is the fix; the server's text says reload.
        if (r.code === 'IDEMPOTENCY_MISMATCH') {
          idem.current = null;
          r.text = ERROR_TEXT.IDEMPOTENCY_MISMATCH;
        }
        if (r.dropped) {
          r.text = `${r.text} If it did reach us, pressing Preview again without edits opens that same change rather than a second one.`;
        }
        setFailure(r);
      })
      .finally(() => {
        setSending(false);
        set_busy(false);
      });
  };

  const render_field = (f) => (
    <ValueField
      key={f.id}
      f={f}
      value={form.values[f.id]}
      error={show(f.id)}
      on_change={(v) => set_value(f.id, v)}
      on_blur={() => touch(f.id)}
    />
  );

  return (
    <form className="tp-form" onSubmit={submit} aria-label="Change this rule" noValidate>
      <p style={{ margin: '0 0 8px' }}>
        <span className="tp-card-field-label">In force now: </span>
        {version.sentence}
      </p>
      <fieldset disabled={sending} style={{ border: 0, padding: 0, margin: 0 }}>
        <h4>Values</h4>
        {values.length ? (
          values.map(render_field)
        ) : (
          <p className="tp-hint">
            This rule has no values to edit here; only its wording can change.
          </p>
        )}
        <h4>Wording</h4>
        {wording.map(render_field)}
        {show('values') ? (
          <div id="rx-values-err" className="tp-err" role="alert" tabIndex={-1}>
            {errors.values}
          </div>
        ) : null}

        <h4>When and why</h4>
        <div className="tp-field">
          <label htmlFor="rx-date">Takes effect from (00:00 IST)</label>
          <input
            id="rx-date"
            type="date"
            className="tp-input"
            min={list.earliest_allowed_date || undefined}
            max={list.latest_allowed_date || undefined}
            value={form.effective_date}
            {...described('rx-date', 'rx-date-hint', show('effective_date'))}
            onChange={(e) => set({ effective_date: e.target.value })}
            onBlur={() => touch('effective_date')}
          />
          <span id="rx-date-hint" className="tp-hint">
            Allowed: {day_label(list.earliest_allowed_date)} to{' '}
            {day_label(list.latest_allowed_date)}. Closed months are frozen. A date before today (
            {day_label(list.today)}) reprices classes already held and needs approval from another
            teacher-payments user.
          </span>
          {show('effective_date') ? (
            <span id="rx-date-err" className="tp-err">
              {errors.effective_date}
            </span>
          ) : null}
          {later.length ? (
            <span className="tp-hint">
              A version of this rule is already scheduled from {day_label(later[0].effective_date)}.
              A change dated before it is refused; pick that date or later.
            </span>
          ) : null}
        </div>
        <div className="tp-field">
          <label htmlFor="rx-note">Note (why the rule changes)</label>
          <textarea
            id="rx-note"
            className="tp-textarea"
            maxLength={LIMITS.note_max_len}
            value={form.note}
            {...described('rx-note', 'rx-note-hint', show('note'))}
            onChange={(e) => set({ note: e.target.value })}
            onBlur={() => touch('note')}
          />
          <span id="rx-note-hint" className="tp-hint">
            {text_length(form.note)}/{LIMITS.note_max_len} characters, at least{' '}
            {LIMITS.note_min_len}. Posted to the team&apos;s records, not to teachers.
          </span>
          {show('note') ? (
            <span id="rx-note-err" className="tp-err">
              {errors.note}
            </span>
          ) : null}
        </div>

        <fieldset className="tp-field" style={{ border: 0, padding: 0 }}>
          <legend style={{ fontWeight: 600 }}>Teacher notice</legend>
          <span className="tp-hint">
            {visible
              ? 'Teachers see this rule. Write the notice they will read, or say why none is needed.'
              : 'Teachers do not see this rule, so a notice is optional.'}
          </span>
          <label>
            <input
              id="rx-notice-yes"
              type="radio"
              name="rx-notice"
              {...notice_mode_aria}
              checked={form.notice_mode === 'notice'}
              onChange={() => set({ notice_mode: 'notice' })}
            />{' '}
            Tell teachers about this change
          </label>
          <label>
            <input
              type="radio"
              name="rx-notice"
              {...notice_mode_aria}
              checked={form.notice_mode === 'none'}
              onChange={() => set({ notice_mode: 'none' })}
            />{' '}
            No notice needed
          </label>
          {!visible ? (
            <label>
              <input
                type="radio"
                name="rx-notice"
                {...notice_mode_aria}
                checked={form.notice_mode === ''}
                onChange={() => set({ notice_mode: '' })}
              />{' '}
              Leave it blank
            </label>
          ) : null}
          {show('notice_mode') ? (
            <span id="rx-notice-mode-err" className="tp-err">
              {errors.notice_mode}
            </span>
          ) : null}
          {form.notice_mode === 'notice' ? (
            <>
              <label htmlFor="rx-notice-text">Notice teachers will read</label>
              <textarea
                id="rx-notice-text"
                className="tp-textarea"
                maxLength={LIMITS.notice_max_len}
                value={form.teacher_notice}
                {...described('rx-notice-text', 'rx-notice-text-hint', show('teacher_notice'))}
                placeholder="e.g. From 20 Oct, you can leave up to 7 minutes before the end without a penalty."
                onChange={(e) => set({ teacher_notice: e.target.value })}
                onBlur={() => touch('teacher_notice')}
              />
              <span id="rx-notice-text-hint" className="tp-hint">
                {text_length(form.teacher_notice)}/{LIMITS.notice_max_len} characters, at least{' '}
                {LIMITS.notice_min_len}.
              </span>
              {show('teacher_notice') ? (
                <span id="rx-notice-text-err" className="tp-err">
                  {errors.teacher_notice}
                </span>
              ) : null}
            </>
          ) : null}
          {form.notice_mode === 'none' ? (
            <>
              <label htmlFor="rx-no-notice">Why no notice is needed</label>
              <textarea
                id="rx-no-notice"
                className="tp-textarea"
                maxLength={LIMITS.no_notice_max_len}
                value={form.no_notice_reason}
                {...described('rx-no-notice', 'rx-no-notice-hint', show('no_notice_reason'))}
                placeholder="e.g. A wording fix; nothing changes for teachers."
                onChange={(e) => set({ no_notice_reason: e.target.value })}
                onBlur={() => touch('no_notice_reason')}
              />
              <span id="rx-no-notice-hint" className="tp-hint">
                {text_length(form.no_notice_reason)}/{LIMITS.no_notice_max_len} characters, at least{' '}
                {LIMITS.no_notice_min_len}.
              </span>
              {show('no_notice_reason') ? (
                <span id="rx-no-notice-err" className="tp-err">
                  {errors.no_notice_reason}
                </span>
              ) : null}
            </>
          ) : null}
        </fieldset>
      </fieldset>

      {approvals.length ? (
        <div className="tp-banner tp-banner-warn" role="note">
          This change will need approval. After the preview, ask for approval: another
          teacher-payments user must approve it before it can be saved:
          <ul style={{ margin: '6px 0 0 18px' }}>
            {approvals.map((a) => (
              <li key={a}>{APPROVAL_REASON_TEXT[a]}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {failure ? (
        <div className="tp-banner tp-banner-error" role="alert">
          <div>{failure.text}</div>
          {failure.problems.length ? (
            <ul style={{ margin: '6px 0 0 18px' }}>
              {failure.problems.map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key -- problems have no id and never reorder
                <li key={i}>{p.message}</li>
              ))}
            </ul>
          ) : null}
          {failure.code === 'RULES_CHANGED' && on_rules_changed ? (
            <button type="button" className="tp-link" onClick={on_rules_changed}>
              Reload the rules
            </button>
          ) : null}
        </div>
      ) : null}
      <p className="tp-hint">
        Preview is a dry run: it saves nothing. You save the change after checking its impact.
      </p>
      <div className="tp-modal-ft">
        <button type="submit" className="tp-btn" disabled={sending}>
          {sending ? 'Starting preview…' : 'Preview impact'}
        </button>
      </div>
    </form>
  );
};

const RuleEditDialog = ({
  version,
  list,
  names,
  catalogue,
  now,
  poll_ms,
  new_request_id = uuidv4,
  prefill: initial_prefill = null,
  on_close,
  on_saved,
  on_rules_changed,
  on_error
}) => {
  const [change, setChange] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // "Prepare again" from this dialog's own change re-opens the form, pre-filled (a new form each time).
  const [prefill, setPrefill] = useState(initial_prefill);
  const [form_rev, setFormRev] = useState(0);
  const prepare_again = (ch) => {
    const again = prepare_again_of(ch, list, catalogue);
    if (!again) return;
    setPrefill(again.prefill);
    setFormRev((r) => r + 1);
    setDirty(false);
    setChange(null);
  };
  const name = (version.rule && version.rule.name) || names[version.rule_key] || '';
  return (
    <TpDialog
      title={`Change rule: ${name}`}
      busy={busy}
      guard_text={
        dirty && !change ? 'You have unsaved edits. Use the close button to discard them.' : ''
      }
      on_close={on_close}
    >
      <div className="tp-modal tp-modal-wide">
        {change ? (
          <ChangePanel
            change_uuid={change.change_uuid}
            initial={change}
            names={names}
            catalogue={catalogue}
            now={now}
            poll_ms={poll_ms}
            on_saved={on_saved}
            on_rules_changed={on_rules_changed}
            on_busy={setBusy}
            on_error={on_error}
            on_prepare_again={prepare_again}
            can_prepare_again={(ch) => !!prepare_again_of(ch, list, catalogue)}
          />
        ) : (
          <EditForm
            key={form_rev}
            version={version}
            list={list}
            catalogue={catalogue}
            prefill={prefill}
            new_request_id={new_request_id}
            on_created={setChange}
            on_rules_changed={on_rules_changed}
            on_error={on_error}
            set_busy={setBusy}
            set_dirty={setDirty}
          />
        )}
      </div>
    </TpDialog>
  );
};

export default RuleEditDialog;
